<template>
  <div>
    <!-- Folder row -->
    <div
      :class="[
        'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-50',
      ]"
      :style="{ paddingLeft: `${depth * 24 + 8}px` }"
      @click="$emit('toggleExpand', folder.path)"
    >
      <!-- Folder checkbox -->
      <div
        :class="[
          'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border-[1.5px] transition-all',
          checkState === 'all'
            ? 'border-green-500 bg-green-500'
            : checkState === 'some'
              ? 'border-green-500 bg-green-100'
              : 'border-neutral-300 bg-white',
        ]"
        @click.stop="$emit('toggleFolder', folder.path)"
      >
        <svg
          v-if="checkState === 'all'"
          class="h-3 w-3 text-white"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          viewBox="0 0 12 12"
        >
          <path d="M2.5 6l2.5 2.5 4.5-5" />
        </svg>
        <svg
          v-else-if="checkState === 'some'"
          class="h-3 w-3 text-green-600"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          viewBox="0 0 12 12"
        >
          <path d="M3 6h6" />
        </svg>
      </div>

      <!-- Expand arrow -->
      <i
        :class="[
          'pi text-[10px]',
          expanded
            ? 'pi-chevron-down text-navy-500'
            : 'pi-chevron-right text-navy-400',
        ]"
      />

      <!-- Folder icon -->
      <i class="pi pi-folder text-navy-400 text-sm" />

      <!-- Folder name + count -->
      <span class="text-navy-800 flex-1 text-sm font-medium">
        {{ folder.name }}
      </span>
      <span class="text-navy-400 text-xs">
        {{ selectedInFolder }}/{{ totalInFolder }}
      </span>
    </div>

    <!-- Expanded content -->
    <div v-if="expanded">
      <!-- Files in this folder (direct children only) -->
      <label
        v-for="doc in folder.documents"
        :key="doc.document_id"
        :class="[
          'flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-all',
          isDocSelected(doc) ? 'bg-green-50' : 'hover:bg-neutral-50',
        ]"
        :style="{ paddingLeft: `${(depth + 1) * 24 + 8}px` }"
      >
        <input
          type="checkbox"
          :checked="isDocSelected(doc)"
          class="sr-only"
          @change="$emit('toggleDocument', doc.document_id)"
        />
        <!-- File checkbox -->
        <div
          :class="[
            'flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border-[1.5px] transition-all',
            isDocSelected(doc)
              ? 'border-green-500 bg-green-500'
              : 'border-neutral-300 bg-white',
          ]"
        >
          <svg
            v-if="isDocSelected(doc)"
            class="h-3 w-3 text-white"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 12 12"
          >
            <path d="M2.5 6l2.5 2.5 4.5-5" />
          </svg>
        </div>

        <!-- File icon -->
        <i class="pi pi-file-pdf text-navy-300 text-sm" />

        <!-- File name + size -->
        <span class="text-navy-700 flex-1 truncate text-sm">
          {{ doc.file_name }}
        </span>
        <span class="text-navy-400 shrink-0 text-xs">
          {{ formatSize(doc.size_bytes) }}
        </span>
      </label>

      <!-- Child folders (recurse) -->
      <DocumentTreeFolder
        v-for="child in folder.children"
        :key="child.path"
        :folder="child"
        :selected-ids="selectedIds"
        :expanded-folders="expandedFolders"
        :depth="depth + 1"
        @toggle-document="$emit('toggleDocument', $event)"
        @toggle-folder="$emit('toggleFolder', $event)"
        @toggle-expand="$emit('toggleExpand', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"

import type { FetchedDocument } from "../../pages/Order/types"

export interface TreeFolder {
  name: string
  path: string
  children: TreeFolder[]
  documents: FetchedDocument[]
}

defineOptions({ name: "DocumentTreeFolder" })

const props = defineProps<{
  folder: TreeFolder
  selectedIds: string[]
  expandedFolders: Set<string>
  depth: number
}>()

defineEmits<{
  toggleDocument: [documentId: string]
  toggleFolder: [folderPath: string]
  toggleExpand: [folderPath: string]
}>()

const expanded = computed(() => props.expandedFolders.has(props.folder.path))

const getAllDocs = (folder: TreeFolder): FetchedDocument[] => {
  const docs = [...folder.documents]
  for (const child of folder.children) {
    docs.push(...getAllDocs(child))
  }
  return docs
}

const allDocs = computed(() => getAllDocs(props.folder))
const totalInFolder = computed(() => allDocs.value.length)
const selectedInFolder = computed(
  () =>
    allDocs.value.filter((d) => props.selectedIds.includes(d.document_id))
      .length,
)

const checkState = computed<"all" | "some" | "none">(() => {
  if (totalInFolder.value === 0) return "none"
  if (selectedInFolder.value === totalInFolder.value) return "all"
  if (selectedInFolder.value > 0) return "some"
  return "none"
})

const isDocSelected = (doc: FetchedDocument) =>
  props.selectedIds.includes(doc.document_id)

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
</script>
